import { useEffect, useMemo, useRef } from 'react';
import { ImagePair } from '@/data/galleryData';
import { useIsMobile } from '@/hooks/use-mobile';
import { ImageReveal } from '@/components/ImageReveal';
import { VideoPlayer } from '@/components/VideoPlayer';
import { getResponsiveProps } from '@/lib/responsiveImage';

interface HorizontalGalleryProps {
  pairs: ImagePair[];
  sectionTitle?: string;
}

type HorizontalItem =
  | { type: 'image'; src: string; key: string; alt: string }
  | { type: 'video'; url: string; displayMode: string; key: string }
  | { type: 'text'; content: string; key: string };

const hasImage = (url: string | undefined) => Boolean(url && url.trim().length > 0);

export function HorizontalGallery({ pairs, sectionTitle }: HorizontalGalleryProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const monoFont = "'IBM Plex Mono', monospace";

  const items = useMemo<HorizontalItem[]>(() => {
    const result: HorizontalItem[] = [];

    pairs.forEach((pair, index) => {
      if (pair.video_url) {
        result.push({ type: 'video', url: pair.video_url, displayMode: pair.video_display_mode || 'viewport', key: `${index}-video` });
      } else {
        if (hasImage(pair.left)) {
          result.push({ type: 'image', src: pair.left, key: `${index}-left`, alt: pair.title || sectionTitle || 'Obra do portfólio' });
        }
        if (hasImage(pair.right) && pair.right !== pair.left) {
          result.push({ type: 'image', src: pair.right, key: `${index}-right`, alt: pair.title || sectionTitle || 'Obra do portfólio' });
        }
      }

      if (pair.title) {
        result.push({ type: 'text', content: pair.title, key: `${index}-text` });
      }
    });

    return result;
  }, [pairs, sectionTitle]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || isMobile) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };

    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [isMobile]);

  return (
    <div
      ref={containerRef}
      className="w-screen h-screen overflow-x-auto overflow-y-hidden bg-background flex"
      
    >
      {items.map((item, i) =>
        item.type === 'image' ? (
          <ImageReveal key={item.key} index={i} className="h-full flex-shrink-0">
            <img
              {...getResponsiveProps(item.src, '100vh')}
              alt={item.alt}
              className="h-full w-auto max-w-none object-cover"
              loading={i === 0 ? 'eager' : 'lazy'}
            />
          </ImageReveal>
        ) : item.type === 'video' ? (
          <ImageReveal key={item.key} index={i} className="h-full flex-shrink-0">
            <div style={{ height: '100%', aspectRatio: '16/9' }}>
              <VideoPlayer url={item.url} displayMode={item.displayMode as 'viewport' | 'background'} />
            </div>
          </ImageReveal>
        ) : (
          <div
            key={item.key}
            className="h-full min-w-[min(70vw,720px)] flex-shrink-0 flex items-center"
            style={{
              padding: 32,
              fontFamily: monoFont,
              fontSize: 16,
              fontWeight: 300,
              color: 'hsl(var(--foreground))',
            }}
          >
            {item.content}
          </div>
        ),
      )}

      {items.length === 0 && <div className="w-screen h-screen bg-muted" />}

      {sectionTitle && (
        <div
          className="fixed z-[50] gallery-title-fade"
          style={{
            bottom: 32,
            left: 32,
            fontFamily: monoFont,
            fontSize: 16,
            fontWeight: 300,
            color: 'hsl(var(--foreground))',
            backgroundColor: 'hsl(var(--background))',
            padding: 8,
          }}
        >
          {sectionTitle}
        </div>
      )}
    </div>
  );
}
